import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, ScrollText, Trophy, Sparkles, Upload } from "lucide-react";
import CardTile, { UnknownTile } from "@/components/CardTile";
import Roulette from "@/components/Roulette";

export default function MinhaConta() {
  const { user, refresh: refreshUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [cards, setCards] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [claiming, setClaiming] = useState(null); // carta a reclamar (modal roleta)

  const [uploadingShipping, setUploadingShipping] = useState(null);

  const loadCards = () => api.get("/cards/mine").then(r => setCards(r.data));
  const loadRedemptions = () => api.get("/redemptions/mine").then(r => setRedemptions(r.data));

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/orders/mine"), api.get("/cards/mine"), api.get("/redemptions/mine"), api.get("/cards/catalog")])
      .then(([o, c, r, cat]) => { setOrders(o.data); setCards(c.data); setRedemptions(r.data); setCatalog(cat.data); });
  }, [user]);

  const uploadShippingProof = async (redemptionId, file) => {
    setUploadingShipping(redemptionId);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/redemptions/${redemptionId}/shipping-proof`, form);
      toast.success("Comprovativo enviado! Aguarda a confirmação do admin.");
      loadRedemptions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a enviar comprovativo");
    } finally {
      setUploadingShipping(null);
    }
  };

  // Agrupa por card_id: cartas repetidas empilham, prémios por reclamar contam-se
  const grouped = {};
  for (const c of cards) {
    (grouped[c.card_id] ??= []).push(c);
  }

  const onClaimFinish = () => {
    setClaiming(null);
    refreshUser();
    loadCards();
  };

  if (!user) return <main className="pt-40 text-center"><p>Inicia sessão para ver a tua conta.</p></main>;

  return (
    <main className="pt-28 pb-20 max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center gap-4 mb-10">
        {user.picture && <img src={user.picture} alt="" className="w-16 h-16 rounded-full border border-gold/40" />}
        <div>
          <h1 className="font-display text-4xl">{user.name}</h1>
          <p className="text-[color:var(--text-2)]">{user.email}</p>
        </div>
        <div className="ml-auto glass px-6 py-4 text-right">
          <div className="text-xs uppercase text-[color:var(--text-3)]">Saldo voucher</div>
          <div className="font-display text-4xl text-gold flex items-center gap-2"><Gift size={22}/> {user.voucher_balance.toFixed(2)}€</div>
        </div>
      </div>

      <Section title="Pedidos" icon={ScrollText}>
        <Table cols={["Data", "Cartas", "Total", "Método", "Estado", ""]}
          rows={orders.map(o => [
            new Date(o.created_at).toLocaleString("pt-PT"),
            `${o.quantity}×`,
            `${o.total_eur.toFixed(2)}€`,
            o.payment_method,
            <span className={`uppercase text-xs ${o.status==="paid"?"text-gold":"text-[color:var(--text-2)]"}`}>{o.status}</span>,
            <button onClick={() => navigate(o.status==="paid" && !o.cards_drawn ? "/sorteio" : `/pagamento/${o.order_id}`)} className="text-gold text-xs underline">
              {o.status==="paid" && !o.cards_drawn ? "Revelar" : "Detalhes"}
            </button>,
          ])} />
      </Section>

      <Section title="Coleção" icon={Trophy}>
        {catalog.length === 0 ? <p className="text-[color:var(--text-3)] text-sm">A carregar…</p> : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {catalog.map(cat => {
              const owned = grouped[cat.id];
              if (!owned) return <UnknownTile key={cat.id} />;
              const claimable = owned.filter(c => c.has_prize);
              return (
                <CardTile key={cat.id} cardId={cat.id} rarity={cat.rarity} hasPrize={claimable.length > 0} count={owned.length} instances={owned}>
                  {claimable.length > 0 && (
                    <button className="btn-primary w-full mt-2 !py-1 text-xs flex items-center justify-center gap-1"
                      onClick={() => setClaiming(claimable[0])} data-testid={`reclamar-button-${cat.id}`}>
                      <Sparkles size={12}/> Reclamar ×{claimable.length}
                    </button>
                  )}
                </CardTile>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Resgates" icon={Gift}>
        <Table cols={["Data", "Prémio", "Valor", "Entrega", "Portes", "Estado"]}
          rows={redemptions.map(r => [
            new Date(r.created_at).toLocaleString("pt-PT"),
            r.prize_name,
            `${(r.total_charged ?? r.prize_price).toFixed(2)}€`,
            r.delivery_method === "shipping"
              ? `Envio (${r.address?.street}, ${r.address?.postal_code} ${r.address?.city})`
              : "Levantamento presencial",
            <ShippingPortesCell r={r} uploading={uploadingShipping === r.redemption_id}
              onUpload={file => uploadShippingProof(r.redemption_id, file)} />,
            <span className="uppercase text-xs">{r.status}</span>,
          ])} />
      </Section>

      {claiming && (
        <Roulette cardInstanceId={claiming.card_instance_id} card={{ name: claiming.card_name }} onFinish={onClaimFinish} />
      )}
    </main>
  );
}

function ShippingPortesCell({ r, uploading, onUpload }) {
  const sp = r.shipping_payment;
  if (!sp) return <span className="text-[color:var(--text-3)]">—</span>;
  if (sp.status === "paid") return <span className="text-gold text-xs">Pagos</span>;
  if (sp.proof_file) return <span className="text-xs text-[color:var(--text-3)]">Aguarda confirmação</span>;
  return (
    <label className="btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1 cursor-pointer">
      <Upload size={12}/> {uploading ? "A enviar…" : `Pagar ${sp.amount.toFixed(2)}€`}
      <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
        disabled={uploading} onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
    </label>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="glass p-6 mb-6">
      <h2 className="font-display text-3xl flex items-center gap-2 mb-4"><Icon size={22} className="text-gold"/> {title}</h2>
      {children}
    </section>
  );
}

function Table({ cols, rows }) {
  if (rows.length === 0) return <p className="text-[color:var(--text-3)] text-sm">Sem registos.</p>;
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-[color:var(--text-3)] text-xs uppercase tracking-widest">{cols.map((c, i) => <th key={i} className="text-left p-2">{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={i} className="border-t border-[color:var(--border)]">
            {r.map((cell, j) => <td key={j} className="p-2">{cell}</td>)}
          </tr>)}
        </tbody>
      </table>
    </div>
  );
}
